import { supabase } from './supabase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

async function getAuthToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export class APIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

async function fetchAPI(endpoint: string, options: RequestInit = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const token = await getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new APIError(
      error.message || 'API request failed',
      response.status,
      error
    );
  }

  return response.json();
}

export async function approveDraft(draftId: string, postId: number, wpAuthorId?: number) {
  return fetchAPI(`/api/drafts/${draftId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ post_id: postId, wp_author_id: wpAuthorId || undefined }),
  });
}

export interface WPAuthor {
  id: number;
  name: string;
  email?: string;
}

export async function listWPAuthors(siteId: string): Promise<WPAuthor[]> {
  return fetchAPI(`/api/drafts/wp-authors/${siteId}`);
}

export interface FeaturedImage {
  url: string | null;
  alt_text?: string;
  width?: number;
  height?: number;
}

export async function getFeaturedImage(draftId: string): Promise<FeaturedImage> {
  return fetchAPI(`/api/drafts/${draftId}/featured-image`);
}

export async function rejectDraft(draftId: string, reason?: string) {
  return fetchAPI(`/api/drafts/${draftId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function publishPost(postId: number) {
  return fetchAPI(`/api/posts/${postId}/publish`, {
    method: 'POST',
  });
}

export async function submitContent(inputText: string, siteId: string, inputType?: string) {
  return fetchAPI('/api/jobs/submit', {
    method: 'POST',
    body: JSON.stringify({
      input_text: inputText,
      site_id: siteId,
      input_type: inputType || undefined,
    }),
  });
}

export async function detectInputType(inputText: string): Promise<string> {
  const data = await fetchAPI('/api/jobs/detect-type', {
    method: 'POST',
    body: JSON.stringify({ input_text: inputText }),
  });
  return data.input_type;
}

export async function listJobs(limit = 10, siteId?: string) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (siteId) params.set('site_id', siteId);
  return fetchAPI(`/api/jobs/list?${params}`);
}

export async function regenerateImage(draftId: string, slot: number, prompt: string) {
  return fetchAPI(`/api/drafts/${draftId}/regenerate-image`, {
    method: 'POST',
    body: JSON.stringify({ slot, prompt }),
  });
}

// Billing API
export async function getBillingUsage() {
  return fetchAPI('/api/billing/usage');
}

export async function createCheckoutSession(priceId: string) {
  return fetchAPI('/api/billing/create-checkout', {
    method: 'POST',
    body: JSON.stringify({ price_id: priceId }),
  });
}

export async function createPortalSession() {
  return fetchAPI('/api/billing/create-portal-session', {
    method: 'POST',
  });
}

// Admin API
export async function getAdminUsageSummary() {
  return fetchAPI('/api/admin/usage-summary');
}

export async function getAdminTeam() {
  return fetchAPI('/api/admin/team');
}

export async function updateTeamMember(userId: string, data: { role?: string; remove?: boolean }) {
  return fetchAPI(`/api/admin/team/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function exportUsageCSV(): Promise<Blob> {
  const token = await getAuthToken();
  const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
  const res = await fetch(`${API_URL}/api/admin/usage-export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new APIError('Export failed', res.status);
  return res.blob();
}

// Org validation (public)
export async function validateOrgSlug(slug: string) {
  return fetchAPI(`/api/billing/validate-org?slug=${encodeURIComponent(slug)}`);
}

// Platform Admin (Super Admin) API
export async function getPlatformOrganizations() {
  return fetchAPI('/api/platform-admin/organizations');
}

export async function getPlatformOrgDetail(orgId: string) {
  return fetchAPI(`/api/platform-admin/organizations/${orgId}`);
}

export async function getPlatformStats() {
  return fetchAPI('/api/platform-admin/stats');
}

export async function getPlatformAuditLog(params?: { org_id?: string; user_id?: string; limit?: number }) {
  const qs = new URLSearchParams();
  if (params?.org_id) qs.set('org_id', params.org_id);
  if (params?.user_id) qs.set('user_id', params.user_id);
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return fetchAPI(`/api/platform-admin/audit-log${query ? `?${query}` : ''}`);
}

// Invite flow
export async function inviteMember(email: string, role: string) {
  return fetchAPI('/api/admin/invite', {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}

// Script Framework Engine — admin viewer (Tasks 21/22)
export interface FrameworkSummary {
  site_id: string;
  site_name: string;
  engine_enabled: boolean;
  framework_id: string | null;
  framework_version: string | null;
  framework_status: string;
  brand_name: string | null;
  supports_youtube: boolean;
}

export async function listAdminFrameworks(): Promise<FrameworkSummary[]> {
  return fetchAPI('/api/admin/frameworks');
}

export async function getAdminFramework(siteId: string): Promise<Record<string, unknown>> {
  return fetchAPI(`/api/admin/frameworks/${encodeURIComponent(siteId)}`);
}

// Billing quota check
export async function checkBillingQuota() {
  return fetchAPI('/api/billing/usage');
}
